import { Entypo, Feather } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import Toast from 'react-native-toast-message';

import { GiftResult } from '@/api/models/giftShowModels';
import { GIFT_DISCOUNT_PERCENTAGE } from '@/constants/config';
import { applyDiscount } from '@/utils/applyDiscount';
import { formatDateYear } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import Text from '../Text';
import GiftShowCard from './GiftShowCard';

type GiftHistoryItemProps = {
  item: GiftResult;
  showData: {
    thumbnail?: string;
    price?: string;
  };
};

const GiftHistoryItem = ({ item, showData }: GiftHistoryItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const height = useSharedValue(0);
  const opacity = useSharedValue(0);
  const rotation = useSharedValue(0);

  const toggle = () => {
    setExpanded(!expanded);
    height.value = withTiming(expanded ? 1 : 0, { duration: 250 });
    opacity.value = withTiming(expanded ? 1 : 0, { duration: 250 });
    rotation.value = withTiming(expanded ? 180 : 0, { duration: 250 });
  };

  const animatedStyle = useAnimatedStyle(() => ({
    height: height.value === 0 ? 0 : 'auto',
    opacity: opacity.value,
    width: '100%',
    overflow: 'visible',
    gap: 5
  }));

  const animatedChevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }]
  }));

  const totalCost = (
    +applyDiscount(parseFloat(showData?.price ?? '0'), GIFT_DISCOUNT_PERCENTAGE) * item.users.length
  ).toFixed(2);

  const handleCopyUrl = async () => {
    const message = i18n.t('giftShows.gifted_copy_url');
    await Clipboard.setStringAsync(message);
    Toast.show({
      type: 'success',
      text1: i18n.t('mix.copied.title'),
      text2: i18n.t('mix.copied.link'),
      position: 'bottom',
      visibilityTime: 2000
    });
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={toggle} activeOpacity={0.8}>
        <View style={styles.header}>
          <View>
            <Text style={styles.text}>
              <Text fontWeight="900" style={styles.boldText}>
                {i18n.t('giftShows.gifted_on')}{' '}
              </Text>
              {formatDateYear(item.createdAt)}
            </Text>
            <Text style={styles.text}>
              <Text fontWeight="900" style={styles.boldText}>
                {i18n.t('giftShows.to')}{' '}
              </Text>
              {item.users.length} {item.users.length === 1 ? 'person' : 'people'}
            </Text>
            {showData.price !== undefined && (
              <Text style={styles.text}>
                <Text fontWeight="900" style={styles.boldText}>
                  {i18n.t('giftShows.total_cost')}{' '}
                </Text>
                ${totalCost}
              </Text>
            )}
          </View>
          <Animated.View style={animatedChevronStyle}>
            <Entypo name="chevron-down" size={24} color="black" />
          </Animated.View>
        </View>
      </TouchableOpacity>

      <Animated.View style={animatedStyle}>
        {item.users.map((gift, index) => (
          <View key={index}>
            <GiftShowCard
              showThumbnail={showData.thumbnail ?? '[]'}
              showPrice={showData.price ?? '-'}
              firstName={gift.firstName}
              lastName={gift.lastName}
              phone={gift.phone}
              textColor="black"
              showSms
            />
            {index !== item.users.length - 1 && <Divider style={styles.divider} />}
          </View>
        ))}
        <BrandedButton style={styles.shareButton} onPress={handleCopyUrl}>
          <View style={styles.shareButtonContent}>
            <Feather name="copy" size={20} color="white" style={styles.icon} />
            <Text fontWeight="700" style={styles.shareLabel}>
              {i18n.t('mix.copy_url')}
            </Text>
          </View>
        </BrandedButton>
      </Animated.View>
    </View>
  );
};

export default GiftHistoryItem;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 8
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 4
  },

  text: {
    fontSize: 16
  },

  boldText: {
    color: 'black'
  },

  divider: {
    marginVertical: 5
  },

  icon: {
    padding: 8
  },

  shareButton: {
    backgroundColor: 'gray',
    marginVertical: 5
  },

  shareLabel: {
    fontSize: 14,
    color: 'white'
  },

  shareButtonContent: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    padding: 5
  }
});
